/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { users } from '../mock-data/users';
import { CcfCxoneLogoIcon } from '@nice-ccf/ui-controls';

export const LOGIN_FEATURE_KEY = 'login';

interface LoginCredentials {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface LoginError {
  message: string;
  status: boolean;
}

interface LoginScreen {
  name: string;
  logoImageSrc: React.ReactNode;
}

const logoStyle = {
  fill: '#ffff',
  width: '150px',
  height: '70px',
  marginLeft: '9rem',
};
export interface LoginState {
  credentials: LoginCredentials;
  error: LoginError;
  screen: LoginScreen;
  isUserLoggedIn: boolean;
  user: any;
  registeredUser: any;
  userDeatils: any;
}

export const initialLoginState: LoginState = {
  credentials: {
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
  },
  error: {
    message: '',
    status: false,
  },
  screen: {
    name: 'usernamelogin',
    logoImageSrc: <CcfCxoneLogoIcon style={logoStyle} />,
  },
  user: {},
  isUserLoggedIn: false,
  registeredUser: [],
  userDeatils: {},
};

export const loginSlice = createSlice({
  name: LOGIN_FEATURE_KEY,
  initialState: initialLoginState,
  reducers: {
    /**
     * Function to navigate to username screen
     * @param state - LoginState
     * @example dispatch(navigateToUsernameScreen());
     * @returns
     */
    navigateToUsernameScreen(state) {
      return {
        ...state,
        credentials: {
          username: '',
          password: '',
          firstName: '',
          lastName: '',
        },
        error: {
          message: '',
          status: false,
        },
        screen: {
          name: 'usernamelogin',
          logoImageSrc: <CcfCxoneLogoIcon style={logoStyle} />,
        },
      };
    },
    /**
     * Function to set username
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setUsername(e.target.value));
     * @returns
     */
    setUsername(state, action: PayloadAction<string>) {
      return {
        ...state,
        credentials: {
          ...state.credentials,
          username: action.payload,
        },
        error: {
          ...state.error,
          message: '',
          status: false,
        },
      };
    },
    /**
     * Function to set password
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setPassword(e.target.value));
     * @returns
     */
    setUserDetails(state, action: PayloadAction<any>) {
      return {
        ...state,
        userDeatils: action.payload,
      };
    },
    /**
     * Function to set password
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setPassword(e.target.value));
     * @returns
     */
    setPassword(state, action: PayloadAction<string>) {
      return {
        ...state,
        credentials: {
          ...state.credentials,
          password: action.payload,
        },
        error: {
          ...state.error,
          message: '',
          status: false,
        },
      };
    },
    /**
     * Function to set s3 registered user
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setS3RegisterdUsers(e.target.value));
     * @returns
     */
    setS3RegisterdUsers(state, action: PayloadAction<string>) {
      return {
        ...state,
        registeredUser: action.payload,
        error: {
          ...state.error,
          message: '',
          status: false,
        },
      };
    },

    /**
     * Function to set password
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setS3RegisterdUsers(e.target.value));
     * @returns
     */
    checkUserNameAndNavigate(state, action: PayloadAction<any>) {
      if (
        users.find(({ username }) => username === state.credentials.username) ||
        state.registeredUser.some(
          (userAccount: any) =>
            userAccount.userName === state.credentials.username &&
            userAccount.state !== 'Inactive'
        )
      ) {
        const response = {
          name: 'passwordlogin',
          logoImageSrc: <CcfCxoneLogoIcon style={logoStyle} />,
        };
        return {
          ...state,
          screen: response,
        };
      } else {
        return {
          ...state,
          credentials: {
            ...state.credentials,
            username: '',
          },
          error: {
            ...state.error,
            message: 'User Not found!',
            status: true,
          },
        };
      }
    },

    /**
     * Function to set password
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setS3RegisterdUsers(e.target.value));
     * @returns
     */
    checkUserNameAndPassword(state, action: PayloadAction<any>) {
      const matchedUser = users.find(
        ({ username, password }) =>
          username === state.credentials.username &&
          password === state.credentials.password
      );
      const matchedS3User = state.registeredUser.find(
        ({ userName, password }: any) =>
          userName === state.credentials.username &&
          password === state.credentials.password
      );

      if (
        users.find(
          ({ username, password }) =>
            username === state.credentials.username &&
            password === state.credentials.password
        ) ||
        state.registeredUser.some(
          (userAccount: any) =>
            userAccount.userName === state.credentials.username &&
            userAccount.password === state.credentials.password
        )
      ) {
        return {
          ...state,
          credentials: {
            ...state.credentials,
            firstName: matchedUser
              ? matchedUser?.firstName
              : matchedS3User?.firstName,
            lastName: matchedUser
              ? matchedUser?.lastName
              : matchedS3User?.lastName,
            email: matchedUser ? matchedUser?.email : matchedS3User?.email,
          },
          user: matchedUser ? matchedUser : matchedS3User,
          screen: {
            name: 'acd',
            logoImageSrc: <CcfCxoneLogoIcon style={logoStyle} />,
          },
          isUserLoggedIn: true,
        };
      } else {
        return {
          ...state,
          user: {},
          screen: {
            name: 'passwordlogin',
            logoImageSrc: <CcfCxoneLogoIcon style={logoStyle} />,
          },
          isUserLoggedIn: false,
          error: {
            ...state.error,
            message: 'Incorrect Password!',
            status: true,
          },
        };
      }
    },

    /**
     * Function to log out user
     * @param state - LoginState
     * @param action - PayloadAction<string>
     * @example dispatch(setPassword(e.target.value));
     * @returns
     */
    logOutUser(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isUserLoggedIn: action.payload,
        credentials: {
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
        },
        user: {},
        screen: {
          name: 'usernamelogin',
          logoImageSrc: <CcfCxoneLogoIcon style={logoStyle} />,
        },
      };
    },
  },
});

export const loginReducer = loginSlice.reducer;
export const loginActions = loginSlice.actions;

/**
 * Function to get login state
 * @param rootState - LoginState
 * @returns It returns login state
 * @example - const loginState = getLoginState(rootState)
 */
const getLoginState = (rootState: { login: LoginState }) => {
  return rootState[LOGIN_FEATURE_KEY];
};

export const selectLoginCredentialsState = createSelector(
  getLoginState,
  (state) => state.credentials
);
export const selectLoginScreenState = createSelector(
  getLoginState,
  (state) => state.screen
);
export const selectLoginErrorState = createSelector(
  getLoginState,
  (state) => state.error
);
export const selectUserLoggedInState = createSelector(
  getLoginState,
  (state) => state.isUserLoggedIn
);

export const getLoggedInUser = createSelector(
  getLoginState,
  (state) => state.userDeatils
);
